import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { authService } from "@/services/authService";
import { getApiErrorMessage } from "@/services/httpClient";
import { clearTokens, hasStoredTokens } from "@/services/tokenStorage";
import { usersService } from "@/services/usersService";
import type { AppRole, UserContextDto, UserDto } from "@/types/auth";

export type UserRole = AppRole | null;

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  username: string;
  phone?: string;
  referralCode?: string;
  referredBy?: string;
  role: UserRole;
  storeId?: number;
  deliveryPartnerId?: number;
  channelPartyId?: number;
  channelLevel?: string;
}

export interface SignupData {
  name: string;
  email: string;
  phone: string;
  password: string;
  referralCode?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<AuthUser | null>;
  adminLogin: (username: string, password: string) => Promise<AuthUser | null>;
  loginWithOtp: (phoneNumber: string, otp: string) => Promise<AuthUser | null>;
  signup: (data: SignupData) => Promise<UserDto | null>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const USER_STORAGE_KEY = "hyperlocal_auth_user";

// Custom event dispatched whenever auth tokens become invalid so the
// AuthProvider can drop the cached user state immediately.
export const AUTH_LOGOUT_EVENT = "hyperlocal:auth-logout";

const AuthContext = createContext<AuthContextValue | null>(null);

function mapContextToAuthUser(context: UserContextDto): AuthUser {
  const { profile, appRole, storeId, deliveryPartnerId, channelPartyId, channelLevel } = context;
  const name =
    [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim() ||
    profile.username ||
    profile.email.split("@")[0];

  const normalizedRole = (appRole?.toLowerCase() ?? "customer") as AppRole;

  return {
    id: String(profile.id),
    name,
    email: profile.email,
    username: profile.username,
    phone: profile.phoneNumber,
    role: normalizedRole,
    storeId,
    deliveryPartnerId,
    channelPartyId,
    channelLevel,
  };
}

function loadStoredUser(): AuthUser | null {
  // Never trust the cached user without a token — defends against tampering
  // and stale sessions from earlier dev-only bypass flows.
  if (!hasStoredTokens()) {
    localStorage.removeItem(USER_STORAGE_KEY);
    return null;
  }
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    localStorage.removeItem(USER_STORAGE_KEY);
    return null;
  }
}

function splitName(fullName: string): { firstName: string; lastName?: string } {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: "" };
  if (parts.length === 1) return { firstName: parts[0] };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(loadStoredUser);
  // Only block the UI when there are tokens but no cached user to render with.
  // With a cached user we show the app immediately and revalidate in the background;
  // a failed revalidation clears the session and ProtectedRoute redirects to login.
  const [isLoading, setIsLoading] = useState(() => hasStoredTokens() && !loadStoredUser());

  const persistUser = useCallback((nextUser: AuthUser | null) => {
    setUser(nextUser);
    if (nextUser) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  }, []);

  const clearSession = useCallback(() => {
    clearTokens();
    persistUser(null);
  }, [persistUser]);

  const refreshUser = useCallback(async () => {
    if (!hasStoredTokens()) {
      clearSession();
      return;
    }

    try {
      const context = await usersService.getMyContext();
      persistUser(mapContextToAuthUser(context));
    } catch {
      clearSession();
    }
  }, [clearSession, persistUser]);

  useEffect(() => {
    if (!hasStoredTokens()) {
      clearSession();
      setIsLoading(false);
      return;
    }

    refreshUser().finally(() => setIsLoading(false));
  }, [clearSession, refreshUser]);

  // Cross-tab sync: when tokens are cleared in another tab, drop the user here too.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === USER_STORAGE_KEY || event.key === "hyperlocal_access_token") {
        if (!hasStoredTokens()) {
          setUser(null);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // The http client dispatches this when token refresh fails — clear local state.
  useEffect(() => {
    const onForcedLogout = () => clearSession();
    window.addEventListener(AUTH_LOGOUT_EVENT, onForcedLogout);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, onForcedLogout);
  }, [clearSession]);

  const login = async (username: string, password: string): Promise<AuthUser | null> => {
    if (!username.trim() || password.length < 6) return null;

    try {
      await authService.login({ username: username.trim(), password });
      const context = await usersService.getMyContext();
      const authUser = mapContextToAuthUser(context);
      persistUser(authUser);
      return authUser;
    } catch {
      clearSession();
      return null;
    }
  };

  const adminLogin = async (username: string, password: string): Promise<AuthUser | null> => {
    if (!username.trim() || password.length < 6) return null;

    try {
      await authService.adminLogin({ username: username.trim(), password });
      const context = await usersService.getMyContext();
      const authUser = mapContextToAuthUser(context);
      if (authUser.role !== "admin") {
        clearSession();
        return null;
      }
      persistUser(authUser);
      return authUser;
    } catch {
      clearSession();
      return null;
    }
  };

  const loginWithOtp = async (
    phoneNumber: string,
    otp: string,
  ): Promise<AuthUser | null> => {
    if (!phoneNumber.trim() || otp.length < 4) return null;

    try {
      await authService.verifyLoginOtp(phoneNumber.trim(), otp);
      const context = await usersService.getMyContext();
      const authUser = mapContextToAuthUser(context);
      persistUser(authUser);
      return authUser;
    } catch {
      clearSession();
      return null;
    }
  };

  const signup = async ({
    name,
    email,
    phone,
    password,
    referralCode,
  }: SignupData): Promise<UserDto | null> => {
    if (!name.trim() || !email.trim() || !phone.trim() || password.length < 6) {
      return null;
    }

    const { firstName, lastName } = splitName(name);

    const registeredUser = await authService.register({
      email: email.trim(),
      password,
      confirmPassword: password,
      firstName,
      lastName,
      phoneNumber: phone.trim(),
      referralCode: referralCode?.trim() || undefined,
    });

    return registeredUser;
  };

  const logout = useCallback(async () => {
    // Clear local state immediately so protected routes redirect right away,
    // even if the server call is slow or fails.
    clearSession();
    try {
      await authService.logout();
    } catch {
      // best-effort: session is already cleared on the client
    }
  }, [clearSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: Boolean(user),
        isLoading,
        login,
        adminLogin,
        loginWithOtp,
        signup,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function roleHomePath(role: UserRole): string {
  switch (role) {
    case "admin":
      return "/admin";
    case "store":
      return "/store";
    case "distributor":
      return "/distributor";
    case "wholesaler":
      return "/wholesaler";
    case "delivery":
      return "/delivery";
    case "customer":
      return "/account";
    default:
      return "/";
  }
}

export function getApiAuthErrorMessage(error: unknown): string {
  return getApiErrorMessage(error, "Authentication failed");
}
